import React, { useEffect, useState } from "react";
import { useSnackbar } from "@components";
import { Image, Tooltip, Button } from "antd";
import { FundViewOutlined } from "@ant-design/icons";
import { workflowTruckById } from "../../../../../src/libs/apis";
import { WorkflowTruckI } from "../../../../../src/libs/interfaces";
import { ContainerDetailModal, InfoSectionI } from "../../../../../src/libs/shared";
import type { Dayjs } from "dayjs";
import laImg from "../../../../images/no-image.jpg";

interface ShowContainerPropsI {
  id: number;
  disabled?: boolean;
}

const noShow = [
  "id",
  "client",
  "producer",
  "farm",
  "zone",
  "balerCode",
  "box",
  "brand",
  "container",
  "clientId",
  "producerId",
  "farmId",
  "magap",
  "boxId",
  "brandId",
  "startProcess",
  "containerArrival",
  "containerOpen",
  "startLoading",
];

interface OnePhotoI {
  src: string | number | boolean | Dayjs | null | undefined;
  alt: string;
}

const infoSections: InfoSectionI[] = [
  {
    title: "General",
    fields: [
      { key: "container", label: "Contenedor" },
      { key: "client", label: "Cliente" },
      { key: "producer", label: "Productor" },
      { key: "farm", label: "Finca" },
      { key: "zone", label: "Zona" },
      { key: "magap", label: "MAGAP" },
      { key: "balerCode", label: "Código Embaladora" },
      { key: "box", label: "Caja" },
      { key: "brand", label: "Marca" },
    ],
  },
  {
    title: "Tiempos",
    fields: [
      { key: "startProcess", label: "Inicio proceso" },
      { key: "closeProcess", label: "Cierre proceso" },
      { key: "endLoading", label: "Fin carga" },
      { key: "containerClose", label: "Cierre contenedor" },
      { key: "containerOulet", label: "Salida contenedor" },
      { key: "hourSaveUser", label: "Hora usuario" },
      { key: "hourSaveServer", label: "Hora servidor" },
    ],
  },
  {
    title: "Carga",
    fields: [
      { key: "numCajas", label: "N° cajas" },
      { key: "cajasEstiba", label: "Cajas por estiba" },
    ],
  },
  {
    title: "Personal",
    fields: [
      { key: "driver", label: "Conductor" },
      { key: "ciConductor", label: "Identificación Conductor" },
      { key: "companyTransport", label: "Compañía Transporte" },
      { key: "placa", label: "Placa Vehicular" },
      { key: "gancho", label: "Gancho" },
      { key: "ganchoCi", label: "Identificación Gancho" },
      { key: "calidad", label: "Calidad" },
      { key: "calidadCi", label: "Identificación Calidad" },
    ],
  },
  {
    title: "Observaciones",
    fields: [
      { key: "observation", label: "Observación" },
    ],
  },
];

export const ShowContainer = ({ id, disabled }: ShowContainerPropsI) => {
  const [open, setOpen] = React.useState(false);
  const [showContainer, setShowContainer] = React.useState(false);

  React.useEffect(() => {
    if (showContainer) {
      setOpen(true);
    }
  }, [showContainer]);

  const handleClose = () => {
    setShowContainer(false);
    setOpen(false);
  };

  return (
    <>
      <Tooltip arrow={false} title="Show">
        <Button
          size="small"
          type="dashed"
          onClick={() => setShowContainer(true)}
          icon={<FundViewOutlined />}
          disabled={disabled}
        />
      </Tooltip>
      {showContainer && (
        <ModalContainer open={open} onClose={handleClose} id={id} />
      )}
    </>
  );
};

interface ModalContainerPropsI {
  open: boolean;
  onClose: () => void;
  id: number;
}

export const ModalContainer = ({ open, onClose, id }: ModalContainerPropsI) => {
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState<WorkflowTruckI>({
    container: "",
  } as WorkflowTruckI);
  const [cards, setCards] = useState<OnePhotoI[]>([]);

  useEffect(() => {
    handleGetData();
  }, []);

  const handleGetData = async () => {
    try {
      const { data: dataContainer } = await workflowTruckById(id);
      if (!dataContainer) return;
      setData(dataContainer);
      const arrKeys = Object.keys(dataContainer).filter(
        (el) => !noShow.includes(el)
      ) as Array<keyof WorkflowTruckI>;

      const keysParents = arrKeys.filter((el) => el.includes("photo")) as any;

      const arrCards: OnePhotoI[] = [];
      for (const x of keysParents) {
        const src = dataContainer[x] as unknown as string;
        if (!src) continue;
        arrCards.push({
          src,
          alt: x,
        });
      }

      setCards(arrCards);

      setLoading(false);
    } catch (err: any) {
      enqueueSnackbar(err.toString(), { variant: "error" });
      setLoading(false);
    }
  };

  return (
    <ContainerDetailModal
      open={open}
      onClose={onClose}
      isLoading={isLoading}
      kind="CAMIÓN"
      container={data.container}
      data={data as any}
      sections={infoSections}
      gallery={
        <>
          {cards.map((el) => (
            <Image
              key={el.alt}
              alt={el.alt}
              src={el.src as string}
              fallback={laImg}
              style={{ width: "100%", height: 200, objectFit: "cover" }}
            />
          ))}
        </>
      }
    />
  );
};
